// Original Author: @rilka, @pato12

import _ from 'lodash';
import React, { useCallback } from 'react';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// TODO: Use new button
// eslint-disable-next-line import-path/forbidden
import Button from 'design-system/ButtonLegacy';
import { requiredAttributesForAnnotation, BreakdownColors } from '@scale/shared/annotation';
import AnnotationAttributeInput from 'react-scale/components/scalers/common/AnnotationAttributeInput';
import {
  LineConfirmationWarning,
  ConfirmationButton,
} from 'react-scale/components/scalers/LineConfirmationWarning';
import { AnnotationsHook } from 'react-scale/hooks/useAnnotations';
import { IInstructionListJson } from '@scale/shared/types/instructionitem';
import {
  Annotation,
  AnnotationAttribute,
  AttributeSelections,
  NERGlobalAttribute,
} from '@scale/shared/types/annotation';
import { AnnotationChangeHandler } from '@scale/shared/annotation/types/AnnotationChange';
import { LabelChoice } from '@scale/shared/types/params/annotation';
import { VideoEvent } from '@scale/shared/types/videoevent';
import { AnnotationGroupingHook } from 'react-scale/hooks/useAnnotationGrouping';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { AttributeFlag } from '@scale/shared/types/ontology';

interface AnnotationAttributesPickerProps {
  assignment?: AssignmentJson;
  // Can be undefined when using this component for global attributes
  annotationId?: string;
  annotationIds?: string[];
  event?: VideoEvent;

  type?: 'compact';
  labelChoices?: LabelChoice[];
  labelSelection?: string;
  attrDefinitions: Record<string, AnnotationAttribute> | Record<string, NERGlobalAttribute>;
  attrSelections: AttributeSelections;
  onChange?: AnnotationChangeHandler;
  isReadOnly?: boolean;
  annotationColor?: string;
  annotationGroupingHook?: AnnotationGroupingHook;
  isLabelRequired?: boolean;
  instructionList?: IInstructionListJson;
  flags?: {
    useSelectForLargeChoiceCount: boolean;
    useSequentialForLargeLabelCount: boolean;
  };
  colorMapping?: $FIXME;
  annotations?: Annotation[];
  breakdownColors?: BreakdownColors;
  selectedBreakdown?: string | null;
  onLinkedAttributeClick?: (annotation: Annotation) => void;
  allowUndo?: boolean;
  undoHint?: string;
  setColorMapping?: $FIXME;
  editableAttributes?: string[];
  dotKeyValueTable?: Record<string, Record<string, string[]>>;
  allowedNumbers?: number[];
  useUnified?: boolean;
  shortcutDisplay?: boolean;
  annotationLabelLocked?: boolean;
  numFrames?: number;
  annotationsHook?: AnnotationsHook;
}

interface AnnotationAttributesPickerState {
  lastValues: AttributeSelections;
}

class AnnotationAttributesPicker extends React.Component<
  AnnotationAttributesPickerProps,
  AnnotationAttributesPickerState
> {
  state: AnnotationAttributesPickerState = {
    lastValues: {},
  };

  undoTimeout?: number;

  componentWillUnmount() {
    if (this.undoTimeout) {
      clearTimeout(this.undoTimeout);
    }
  }

  /* ----- Read methods ----- */

  getAttributesToRender() {
    if (_.isEmpty(this.props.attrDefinitions)) {
      return [];
    }

    const requiredAttrs = requiredAttributesForAnnotation({
      annotation: {
        label: this.props.labelSelection,
        attributes: this.props.attrSelections,
        // If the annotation ID is not set, this is for globals
        isGlobal: !this.props.annotationId,
      },
      attributeDefinitions: this.props.attrDefinitions,
    });

    return _.keys(requiredAttrs);
  }

  getAttributeFlags(attributesToRender: string[]) {
    if (this.props.event) {
      return _.pick(this.props.event.attributeFlags, attributesToRender);
    }

    if (!this.props.annotationsHook || !attributesToRender.length) {
      return;
    }

    // If only one annotation is selected
    if (!this.props.annotationIds) {
      return this.props.annotationId
        ? this.props.annotationsHook.findById(this.props.annotationId)?.attributeFlags
        : undefined;
    }

    const remainingAttributes = new Set(attributesToRender);
    const unconfirmedAttributes = new Set(attributesToRender);

    for (const id of this.props.annotationIds) {
      const annotation = this.props.annotationsHook.findById(id);
      const attributeFlags = annotation?.attributeFlags;
      if (!attributeFlags) continue;

      for (const attr of remainingAttributes) {
        if (attr in attributeFlags) remainingAttributes.delete(attr);
      }
      for (const attr of unconfirmedAttributes) {
        if (attr in attributeFlags && !attributeFlags[attr].confirmed) {
          unconfirmedAttributes.delete(attr);
        }
      }

      if (!remainingAttributes.size && !unconfirmedAttributes.size) break;
    }

    const flaggedAttributes = attributesToRender.filter(attr => !remainingAttributes.has(attr));
    if (!flaggedAttributes.length) return;

    return flaggedAttributes.reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          descriptions: [`${key} was flagged in one or more of your selected annotations`],
          confirmed: unconfirmedAttributes.has(key),
        },
      }),
      {},
    ) as Record<string, AttributeFlag>;
  }

  handleLabelSelect = (_labelKey: string, labelVal: string | number | undefined) => {
    if (!_.isString(labelVal) || !this.props.onChange) {
      return;
    }
    this.props.onChange(this.props.annotationId, { labelVal });
  };

  handleAttrSelect = (attrKey: string, attrVal: string | string[] | number | undefined) => {
    const oldValue = this.props.attrSelections[attrKey];
    if (this.props.onChange) {
      this.props.onChange(this.props.annotationId, { attrKey, attrVal });
    }

    // Undo logic
    // reset the others values
    this.setState({ lastValues: { [attrKey]: oldValue } });
    // and close after 5 seconds
    if (this.undoTimeout) {
      window.clearTimeout(this.undoTimeout);
    }
    this.undoTimeout = window.setTimeout(this.closeUndoWarning, 5 * 1000);
  };

  closeUndoWarning = () => {
    this.setState({ lastValues: {} });
  };

  undoValue = (attrKey: string) => {
    const attrVal = this.state.lastValues[attrKey];
    if (attrVal && this.props.onChange) {
      this.props.onChange(this.props.annotationId, { attrKey, attrVal });
      this.closeUndoWarning();
    }
  };

  /* ----- Rendering ----- */

  renderLabel() {
    const labelParams = {
      type: 'label',
      description: 'Label',
      choices: this.props.labelChoices,
    };
    return (
      <AnnotationAttributeInput
        isReadOnly={this.props.isReadOnly || !!this.props.annotationLabelLocked}
        key={`${this.props.annotationId} label`}
        attrKey="label"
        attrParams={labelParams}
        selectedChoice={this.props.labelSelection}
        handleUpdateSelection={this.handleLabelSelect}
        handleLinkedAttributeClick={this.props.onLinkedAttributeClick}
        instructionList={this.props.instructionList}
        flags={this.props.flags}
      />
    );
  }

  /* ---------- Undo message ------------------------------------------------ */
  renderUndo = (attrKey: string) => {
    const oldValue = this.state.lastValues[attrKey];
    if (!oldValue || !this.props.allowUndo) {
      return null;
    }
    return (
      <LineConfirmationWarning
        key={`${attrKey}:${oldValue}`}
        text={this.props.undoHint || 'The change has been applied'}
        buttons={<ConfirmationButton text="Undo" onClick={() => this.undoValue(attrKey)} />}
      />
    );
  };

  renderAttribute = (attrKey: string, attributeFlag?: AttributeFlag) => {
    const isEditable = this.props.editableAttributes
      ? this.props.editableAttributes.includes(attrKey)
      : true;

    let colorMapping = null;
    if (
      this.props.colorMapping &&
      _.isEqual(this.props.colorMapping.key, ['attributes', attrKey])
    ) {
      colorMapping = this.props.colorMapping.mapping;
    }

    return (
      <React.Fragment key={`${this.props.annotationId} ${attrKey}`}>
        <AnnotationAttributeInput
          assignment={this.props.assignment}
          type={this.props.type}
          isReadOnly={this.props.isReadOnly || !isEditable}
          attrKey={attrKey}
          attrParams={this.props.attrDefinitions[attrKey]}
          selectedChoice={this.props.attrSelections[attrKey]}
          handleUpdateSelection={this.handleAttrSelect}
          handleLinkedAttributeClick={this.props.onLinkedAttributeClick}
          annotationId={this.props.annotationId}
          annotations={this.props.annotations}
          attrDefinitions={this.props.attrDefinitions}
          breakdownColors={this.props.breakdownColors}
          label={this.props.labelSelection}
          selectedBreakdown={this.props.selectedBreakdown}
          colorMapping={colorMapping}
          setColorMapping={this.props.setColorMapping}
          flags={this.props.flags}
          dotKeyValueTable={this.props.dotKeyValueTable}
          allowedNumbers={this.props.allowedNumbers}
          useUnified={this.props.useUnified}
          shortcutDisplay={this.props.shortcutDisplay}
          numFrames={this.props.numFrames}
          attributeFlag={attributeFlag}
        />
        {this.renderUndo(attrKey)}
      </React.Fragment>
    );
  };

  render() {
    const attributesToRender = this.getAttributesToRender();
    const attributeFlags = this.getAttributeFlags(attributesToRender);

    return (
      <div className="attributes-picker">
        {this.props.isLabelRequired && this.renderLabel()}
        {attributesToRender.map(attribute =>
          this.renderAttribute(attribute, attributeFlags?.[attribute]),
        )}
        {this.props.annotationGroupingHook?.isGroupingEnabled && this.props.annotationId && (
          <AttributeGroupingInput
            annotationId={this.props.annotationId}
            annotationGroupingHook={this.props.annotationGroupingHook!}
            annotationColor={this.props.annotationColor}
          />
        )}
      </div>
    );
  }
}

export default AnnotationAttributesPicker;

interface AttributeGroupingInputProps {
  annotationId: string;
  annotationColor?: string;
  annotationGroupingHook: AnnotationGroupingHook;
}

export const AttributeGroupingInput = ({
  annotationId,
  annotationColor,
  annotationGroupingHook,
}: AttributeGroupingInputProps) => {
  const { selectedGroup, handleGroupingStart, handleGroupingCancel } = annotationGroupingHook;

  const handleStart = useCallback(() => {
    if (handleGroupingStart) {
      handleGroupingStart(annotationId);
    }
  }, [annotationId, handleGroupingStart]);

  const handleCancel = useCallback(() => {
    if (handleGroupingCancel) {
      handleGroupingCancel();
    }
  }, [annotationId, handleGroupingCancel]);

  const isGroupingActive = selectedGroup === annotationId;

  return (
    <div className="grouping">
      {isGroupingActive ? (
        <React.Fragment>
          <strong>
            <span>Fragments &nbsp;</span>
            <span className="active">Active</span>
          </strong>

          <Button size="sm" onClick={handleCancel}>
            Done
          </Button>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <strong
            data-for="fragments"
            data-tip="Use fragments when your annotation is split into multiple pieces"
          >
            <span>Fragments &nbsp;</span>
            <FontAwesomeIcon icon="question-circle" />
          </strong>

          <Button size="sm" onClick={handleStart}>
            <FontAwesomeIcon icon="plus-circle" />
            <span>&nbsp; Add</span>
          </Button>

          <ReactTooltip id="fragments" />
        </React.Fragment>
      )}
      <style jsx>{`
        .grouping {
          margin: 12px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .active {
          display: inline-block;
          padding: 4px 8px;
          background: ${annotationColor || 'inherit'};
          border-radius: var(--border-radius);
          color: white;
        }
      `}</style>
    </div>
  );
};
